/**
 * Detects whether the current browser is Chromium-based. The talent-facing
 * pages are Chromium-only because the WebAudio stack (MediaStream tap,
 * OfflineAudioContext exports, MediaRecorder) has known Safari quirks the
 * React app routes around but can't fully hide, and Firefox is an untested
 * path.
 *
 * Strategy — positively detect Chromium, block everything else. "Detect
 * Safari" is unreliable; allow-Chromium is the robust pattern (mirrors
 * `TRO Spheres Builder/src/components/BrowserGate.tsx`):
 *   1. navigator.userAgentData with a Chromium brand → allowed.
 *   2. "Chrome/" token in userAgent → allowed (Chrome, Edge, Brave, Opera,
 *      Vivaldi all contain it). Firefox + Safari do not → blocked. Legacy
 *      EdgeHTML ("Edge/" without "Chrome/") is excluded. iOS Chrome
 *      ("CriOS") and modern iPad Safari ("Macintosh" UA) lack "Chrome/"
 *      → blocked (the tool needs desktop Chromium anyway).
 *
 * Called synchronously during render — no useEffect — so no WebAudio code
 * path mounts on an unsupported browser.
 */
export function isSupportedBrowser(): boolean {
  if (typeof navigator === 'undefined') return true; // SSR safety
  // Internal-test escape hatch: ?bypass-browser-check=1
  if (
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('bypass-browser-check') === '1'
  ) {
    return true;
  }
  const uaData = (navigator as unknown as {
    userAgentData?: { brands?: Array<{ brand: string }> };
  }).userAgentData;
  if (uaData?.brands?.some((b) => b.brand === 'Chromium' || b.brand === 'Google Chrome')) {
    return true;
  }
  return /Chrome\//.test(navigator.userAgent);
}
